<template>
  <div class="multi-text-field-response">
    <div class="field-header">
      <h3 class="field-label">
        {{ field.label }}
        <span v-if="field.required" class="required-indicator">*</span>
      </h3>
    </div>

    <div v-if="field.description" class="field-description">
      {{ field.description }}
    </div>

    <div class="text-entries">
      <div
        v-for="(entry, index) in textEntries"
        :key="'entry-' + index"
        class="text-entry-item"
      >
        <div class="entry-header">
          <span class="entry-number">{{ index + 1 }}</span>
          <v-btn
            icon
            x-small
            class="delete-btn"
            @click="removeEntry(index)"
            :disabled="(textEntries.length <= 1 && field.required) || disabled"
          >
            <v-icon x-small>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-text-field
          v-for="(template, templateIndex) in field.textTemplates"
          :key="'template-' + templateIndex"
          v-model="entry.values[template.id || templateIndex]"
          outlined
          :readonly="disabled"
          dense
          :label="(template.label ?? 'Label') + (template.required ? ' *' : '')"
          :placeholder="template.placeholder ?? 'Enter value'"
          class="mb-2"
          @input="updateResponseData"
        ></v-text-field>
      </div>
    </div>

    <div class="validation-error error--text mb-2" v-if="showValidationError">
      {{ validationErrorMessage }}
    </div>

    <div class="actions-row mb-4">
      <v-btn
        small
        color="primary"
        outlined
        @click="addEntry"
        :disabled="hasReachedMaxEntries || disabled"
      >
        <v-icon small left>mdi-plus</v-icon>
        Add Entry
      </v-btn>

      <div v-if="hasMaxEntries" class="entries-limit">
        <v-icon x-small color="grey">mdi-information-outline</v-icon>
        <span>Maximum {{ field.maxEntries }} entries</span>
      </div>
    </div>

    <v-textarea
      v-model="localResponse.comment"
      outlined
      dense
      label="Comment (Optional)"
      placeholder="Add any additional comments here..."
      rows="2"
      auto-grow
      @change="updateResponseData"
      :readonly="disabled"
    ></v-textarea>
  </div>
</template>

<script>
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Field } from '@/shared/types/aperTemplate';
import { ResponseDTO } from '@/shared/apis/aper/aperEvaluations';

// interface MultiTextEntry {
//   values: Record<string, string>;
// }

@Component
export default class MultiTextFieldResponse extends Vue {
  @Prop({ type: Object, required: true }) field;
  @Prop({ type: Object, required: false }) response;
  @Prop({ type: Boolean, required: false }) disabled;

  localResponse = {
    fieldId: 0,
    textValues: [],
    comment: ''
  };

  textEntries = [];
  showValidationError = false;
  validationErrorMessage = '';

  created() {
    this.initializeResponse();
  }

  @Watch('response')
  onResponseChange() {
    this.initializeResponse();
    //console.log("Entries", this.textEntries);
  }

  get hasMinEntries() {
    return this.field.minEntries !== undefined && this.field.minEntries > 0;
  }

  get hasMaxEntries() {
    return this.field.maxEntries !== undefined && this.field.maxEntries > 0;
  }

  get minEntries() {
    return this.field.minEntries !== undefined ? this.field.minEntries : 0;
  }

  get maxEntries() {
    return this.field.maxEntries !== undefined ? this.field.maxEntries : Infinity;
  }

  get hasReachedMaxEntries() {
    return this.hasMaxEntries && this.textEntries.length >= this.maxEntries;
  }

  initializeResponse() {
    this.localResponse = {
      fieldId: this.field.id || 0,
      textValues: [],
      comment: (this.response && this.response.comment) || ''
    };

    // Initialize entries based on existing response or create new ones
    if (this.response && this.response.textValues && this.response.textValues.length > 0) {
      // Convert existing TextValueDTO[] to MultiTextEntry[] format
      this.textEntries = this.convertTextValuesToEntries(this.response.textValues);
    } else {
      // Create initial empty entries
      const initialEntryCount = Math.max(1, this.minEntries);
      this.textEntries = [];

      for (let i = 0; i < initialEntryCount; i++) {
        this.textEntries.push(this.createEmptyEntry());
      }
    }

    this.updateResponseData();
  }

  convertTextValuesToEntries(textValues) {
    // Group text values by entry (assuming they follow a pattern)
    // For now, create entries based on the existing structure
    const entries = [];

    if (!this.field.textTemplates || this.field.textTemplates.length === 0) {
      // Fallback: treat each textValue as a separate entry
      textValues.forEach(textValue => {
        entries.push({
          values: { '0': textValue.value }
        });
      });
    } else {
      // Group values by templates - this assumes textValues are ordered per template per entry
      const templatesCount = this.field.textTemplates.length;
      const entriesCount = Math.ceil(textValues.length / templatesCount);

      for (let entryIndex = 0; entryIndex < entriesCount; entryIndex++) {
        const entry = { values: {} };

        this.field.textTemplates.forEach((template, templateIndex) => {
          const valueIndex = entryIndex * templatesCount + templateIndex;
          const templateId = template.id || templateIndex.toString();
          entry.values[templateId] = (textValues[valueIndex] && textValues[valueIndex].value) || '';
        });

        entries.push(entry);
      }
    }

    return entries;
  }

  createEmptyEntry() {
    const entry = { values: {} };

    if (this.field.textTemplates && this.field.textTemplates.length > 0) {
      // Initialize values for each template
      this.field.textTemplates.forEach((template, index) => {
        const templateId = template.id || index.toString();
        entry.values[templateId] = '';
      });
    } else {
      // Fallback: single value
      entry.values['0'] = '';
    }

    return entry;
  }

  addEntry() {
    if (!this.hasReachedMaxEntries) {
      this.textEntries.push(this.createEmptyEntry());
      this.updateResponseData();
    }
  }

  removeEntry(index) {
    if (this.textEntries.length > 1 || !this.field.required) {
      this.textEntries.splice(index, 1);
      this.updateResponseData();
    }
  }

  validateEntries() {
    // Check if meets minimum entries requirement
    if (this.hasMinEntries && this.textEntries.length < this.minEntries) {
      this.showValidationError = true;
      this.validationErrorMessage = `At least ${this.minEntries} entries are required`;
      return false;
    }

    // Check if any required field is empty
    if (this.field.required || (this.field.textTemplates && this.field.textTemplates.some(template => template.required))) {
      const hasEmptyRequiredFields = this.textEntries.some(entry => {
        if (this.field.textTemplates && this.field.textTemplates.length > 0) {
          return this.field.textTemplates.some(template => {
            if (template.required) {
              const templateId = template.id || this.field.textTemplates.indexOf(template).toString();
              return !entry.values[templateId] || !entry.values[templateId].trim();
            }
            return false;
          });
        } else {
          // Fallback check
          return !entry.values['0'] || !entry.values['0'].trim();
        }
      });

      if (hasEmptyRequiredFields) {
        this.showValidationError = true;
        this.validationErrorMessage = 'All required fields must be filled in';
        return false;
      }
    }

    this.showValidationError = false;
    this.validationErrorMessage = '';
    return true;
  }

  updateResponseData() {
    this.validateEntries();

    // Convert MultiTextEntry[] to TextValueDTO[] format
    const textValues = [];

    this.textEntries.forEach((entry, entryIndex) => {
      if (this.field.textTemplates && this.field.textTemplates.length > 0) {
        this.field.textTemplates.forEach((template, templateIndex) => {
          const templateId = template.id || templateIndex.toString();
          const value = entry.values[templateId] || '';

          textValues.push({
            label: `${template.label} (Entry ${entryIndex + 1})`,
            value: value
          });
        });
      } else {
        // Fallback for entries without templates
        const value = entry.values['0'] || '';
        textValues.push({
          label: `Entry ${entryIndex + 1}`,
          value: value
        });
      }
    });

    this.localResponse.textValues = textValues;
    this.$emit('update:response', this.localResponse);
  }
}
</script>

<style scoped>
.multi-text-field-response {
  width: 100%;
}

.field-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.field-label {
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin: 0 0 4px 0;
}

.required-indicator {
  color: #f44336;
  margin-left: 4px;
}

.field-description {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 12px;
}

.text-entries {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
}

.text-entry-item {
  background-color: #f5f7fa;
  border-radius: 8px;
  padding: 16px;
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.entry-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.entry-number {
  font-size: 0.85rem;
  font-weight: 500;
  color: #3f51b5;
  background-color: rgba(63, 81, 181, 0.1);
  padding: 2px 8px;
  border-radius: 12px;
}

.delete-btn {
  opacity: 0.6;
}

.delete-btn:hover {
  opacity: 1;
}

.actions-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.entries-limit {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  gap: 4px;
}

.validation-error {
  font-size: 0.8rem;
}
</style>
